import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { ParsedCSVResponse } from 'shared';
import { bankImportApi } from '../../api/bankImport';

interface UploadStepProps {
  onParsed: (data: ParsedCSVResponse) => void;
}

export const UploadStep: React.FC<UploadStepProps> = ({ onParsed }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await bankImportApi.parseCSV(file);
      onParsed(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to parse CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div data-component="bank-import-upload-step" style={{ display: 'block', textAlign: 'center', padding: '2rem' }}>
      <div style={{ display: 'block', maxWidth: '500px', margin: '0 auto' }}>
        <i className="pi pi-file-import" style={{ fontSize: '3rem', color: 'var(--primary-color)', display: 'block', marginBottom: '1rem' }} />
        <h3 style={{ margin: '0 0 0.5rem 0' }}>Upload Bank CSV Export</h3>
        <p style={{ color: 'var(--text-color-secondary)', margin: '0 0 1.5rem 0' }}>
          Upload a CSV export from your bank (e.g. Skatbank) to import transactions.
        </p>

        {uploading ? (
          <div style={{ marginBottom: '1rem' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
            <p style={{ color: 'var(--text-color-secondary)' }}>Parsing CSV file...</p>
          </div>
        ) : (
          <>
            <div
              style={{
                border: '2px dashed',
                borderColor: dragOver ? 'var(--primary-color)' : 'var(--surface-300)',
                borderRadius: '6px',
                padding: '2rem',
                cursor: 'pointer',
                marginBottom: '1rem',
                background: dragOver ? 'var(--surface-100)' : 'transparent',
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="pi pi-cloud-upload" style={{ fontSize: '2rem', color: 'var(--surface-400)', display: 'block', marginBottom: '0.75rem' }} />
              <p style={{ color: 'var(--text-color-secondary)', margin: 0 }}>
                Drag & drop a CSV file here, or click to browse
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <Button
              label="Select CSV File"
              icon="pi pi-upload"
              onClick={() => fileInputRef.current?.click()}
            />
          </>
        )}

        {error && <Message severity="error" text={error} style={{ display: 'block', marginTop: '1rem' }} />}
      </div>
    </div>
  );
};
